import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { wasteManagementService } from '../services';
import { useAuthStore } from '../store/authStore';

type WasteType = 'cut_waste' | 'cotton_waste';
type Section = 'digital' | 'rotary' | 'paste' | 'post_production' | 'other';
type AnalyticsPeriod = 'monthly' | 'yearly';

interface CutWasteRecord {
  id: number;
  waste_type: WasteType;
  section: Section;
  other_section_name: string | null;
  weight_kg: number;
  created_by_username: string;
  created_at: string;
}

interface AnalyticsTrendRow {
  bucket: number;
  waste_type: WasteType;
  record_count: number;
  total_weight_kg: number;
}

interface AnalyticsSectionRow {
  section: Section;
  other_section_name: string | null;
  record_count: number;
  total_weight_kg: number;
}

interface CutWasteAnalytics {
  summary: {
    record_count: number;
    total_weight_kg: number;
    average_weight_kg: number;
  };
  trend: AnalyticsTrendRow[];
  byType: Array<{
    waste_type: WasteType;
    record_count: number;
    total_weight_kg: number;
  }>;
  bySection: AnalyticsSectionRow[];
}

const WASTE_TYPE_LABELS: Record<WasteType, string> = {
  cut_waste: 'Cut waste',
  cotton_waste: 'Cotton waste',
};

const SECTION_LABELS: Record<Exclude<Section, 'other'>, string> = {
  digital: 'Digital',
  rotary: 'Rotary',
  paste: 'Paste',
  post_production: 'Post production',
};
const SECTION_ORDER: Exclude<Section, 'other'>[] = [
  'digital',
  'rotary',
  'paste',
  'post_production',
];

const sectionLabel = (record: Pick<CutWasteRecord, 'section' | 'other_section_name'>) =>
  record.section === 'other'
    ? record.other_section_name || 'Other'
    : SECTION_LABELS[record.section];

const CutWasteManagement: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [activeTab, setActiveTab] = useState(0);
  const [wasteType, setWasteType] = useState<WasteType | ''>('');
  const [sectionWeights, setSectionWeights] = useState<Record<Exclude<Section, 'other'>, string>>({
    digital: '0',
    rotary: '0',
    paste: '0',
    post_production: '0',
  });
  const [otherSectionName, setOtherSectionName] = useState('');
  const [otherWeightKg, setOtherWeightKg] = useState('0');
  const [records, setRecords] = useState<CutWasteRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [period, setPeriod] = useState<AnalyticsPeriod>('monthly');
  const [year, setYear] = useState(new Date().getFullYear());
  const [analytics, setAnalytics] = useState<CutWasteAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadRecords = useCallback(async () => {
    try {
      setRecordsLoading(true);
      const response = await wasteManagementService.getCutWasteRecords();
      setRecords(response.records);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to load cut waste records');
    } finally {
      setRecordsLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      setAnalyticsLoading(true);
      setAnalytics(null);
      setError(null);
      const response = await wasteManagementService.getCutWasteAnalytics({
        period,
        year: period === 'monthly' ? year : undefined,
      });
      setAnalytics(response);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to load cut waste analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  }, [period, year]);

  useEffect(() => {
    void loadRecords();
  }, [loadRecords]);

  useEffect(() => {
    if (activeTab === 1) void loadAnalytics();
  }, [activeTab, loadAnalytics]);

  const submitRecord = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!wasteType) {
      setError('Select a waste type.');
      return;
    }

    const entries: Array<{ section: Section; weight_kg: number }> = SECTION_ORDER
      .map((selectedSection) => ({
        section: selectedSection,
        weight_kg: Number(sectionWeights[selectedSection] || 0),
      }));
    const otherWeight = Number(otherWeightKg || 0);
    if (entries.some((entry) => !Number.isFinite(entry.weight_kg) || entry.weight_kg < 0 || entry.weight_kg > 999999999.999)
      || !Number.isFinite(otherWeight) || otherWeight < 0 || otherWeight > 999999999.999) {
      setError('Enter valid non-negative weights up to 999,999,999.999 kg.');
      return;
    }
    if (otherWeight > 0 && !otherSectionName.trim()) {
      setError('Enter the name of the section.');
      return;
    }
    if (otherWeight > 0) {
      entries.push({
        section: 'other',
        weight_kg: otherWeight,
      });
    }
    const submittedEntries = entries.filter((entry) => entry.weight_kg > 0).map((entry) => ({
      ...entry,
      other_section_name: entry.section === 'other' ? otherSectionName.trim() : undefined,
    }));
    if (submittedEntries.length === 0) {
      setError('Enter a weight greater than zero for at least one section.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setMessage(null);
      const response = await wasteManagementService.createCutWasteRecords({
        waste_type: wasteType,
        entries: submittedEntries,
      });
      setWasteType('');
      setSectionWeights({
        digital: '0',
        rotary: '0',
        paste: '0',
        post_production: '0',
      });
      setOtherSectionName('');
      setOtherWeightKg('0');
      setMessage(`${response.count} cut waste ${response.count === 1 ? 'record' : 'records'} saved.`);
      await loadRecords();
      if (activeTab === 1) await loadAnalytics();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.error || 'Failed to save cut waste record');
    } finally {
      setSubmitting(false);
    }
  };

  const trendData = useMemo(() => {
    const buckets = period === 'monthly'
      ? Array.from({ length: 12 }, (_, index) => ({
          bucket: index + 1,
          label: new Date(2000, index).toLocaleString('en', { month: 'short' }),
          cut_waste: 0,
          cotton_waste: 0,
        }))
      : (analytics?.trend || [])
          .map((row) => row.bucket)
          .filter((bucket, index, values) => values.indexOf(bucket) === index)
          .sort((a, b) => a - b)
          .map((bucket) => ({
            bucket,
            label: String(bucket),
            cut_waste: 0,
            cotton_waste: 0,
          }));

    (analytics?.trend || []).forEach((row) => {
      const bucket = buckets.find((item) => item.bucket === row.bucket);
      if (bucket) bucket[row.waste_type] = row.total_weight_kg;
    });
    return buckets;
  }, [analytics, period]);

  const sectionData = useMemo(
    () => (analytics?.bySection || []).map((row) => ({
      name: row.section === 'other'
        ? row.other_section_name || 'Other'
        : SECTION_LABELS[row.section],
      weight_kg: row.total_weight_kg,
      record_count: row.record_count,
    })),
    [analytics]
  );

  const yearOptions = Array.from(
    { length: Math.max(1, new Date().getFullYear() - 2000 + 1) },
    (_, index) => new Date().getFullYear() - index
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2 }}>Cut waste</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record cut and cotton waste by production section. The signed-in username and submission time are saved automatically.
      </Typography>

      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)}>
          <Tab label="Waste log" />
          <Tab label="Analytics" />
        </Tabs>
      </Paper>

      {activeTab === 0 && (
        <>
          <Paper component="form" onSubmit={submitRecord} sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Add waste records together</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the waste type once and enter each section weight. Rows left at 0 kg are not saved as empty records.
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth required>
                  <InputLabel id="cut-waste-type-label">Waste type</InputLabel>
                  <Select
                    labelId="cut-waste-type-label"
                    label="Waste type"
                    value={wasteType}
                    onChange={(event) => setWasteType(event.target.value as WasteType)}
                  >
                    <MenuItem value="cut_waste">Cut waste</MenuItem>
                    <MenuItem value="cotton_waste">Cotton waste</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {SECTION_ORDER.map((selectedSection) => (
                <Grid item xs={12} sm={6} md={4} key={selectedSection}>
                  <TextField
                    fullWidth
                    label={`${SECTION_LABELS[selectedSection]} weight`}
                    type="number"
                    value={sectionWeights[selectedSection]}
                    onChange={(event) => setSectionWeights((current) => ({
                      ...current,
                      [selectedSection]: event.target.value,
                    }))}
                    inputProps={{ min: 0, max: 999999999.999, step: 0.001 }}
                    InputProps={{ endAdornment: <Typography color="text.secondary">kg</Typography> }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Other section name"
                  value={otherSectionName}
                  onChange={(event) => setOtherSectionName(event.target.value)}
                  inputProps={{ maxLength: 191 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Other section weight"
                  type="number"
                  value={otherWeightKg}
                  onChange={(event) => setOtherWeightKg(event.target.value)}
                  inputProps={{ min: 0, max: 999999999.999, step: 0.001 }}
                  InputProps={{ endAdornment: <Typography color="text.secondary">kg</Typography> }}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting ? <CircularProgress size={22} color="inherit" /> : 'Submit all sections'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Recorded for {user?.username || 'your account'} · date and time assigned on submit
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="h6" sx={{ mb: 1 }}>Waste records</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date and time</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Waste type</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell align="right">Weight (kg)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{new Date(record.created_at).toLocaleString()}</TableCell>
                    <TableCell>{record.created_by_username}</TableCell>
                    <TableCell>{WASTE_TYPE_LABELS[record.waste_type]}</TableCell>
                    <TableCell>{sectionLabel(record)}</TableCell>
                    <TableCell align="right">{record.weight_kg.toFixed(3)}</TableCell>
                  </TableRow>
                ))}
                {!recordsLoading && records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">No cut waste records have been logged.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {recordsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>}
        </>
      )}

      {activeTab === 1 && (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="cut-waste-period-label">Analytics period</InputLabel>
                <Select
                  labelId="cut-waste-period-label"
                  label="Analytics period"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value as AnalyticsPeriod)}
                >
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
              {period === 'monthly' && (
                <FormControl sx={{ minWidth: 140 }}>
                  <InputLabel id="cut-waste-year-label">Year</InputLabel>
                  <Select
                    labelId="cut-waste-year-label"
                    label="Year"
                    value={year}
                    onChange={(event) => setYear(Number(event.target.value))}
                  >
                    {yearOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Paper>

          {analyticsLoading && !analytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : analytics && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary">Total waste</Typography>
                    <Typography variant="h4">{analytics.summary.total_weight_kg.toFixed(3)} kg</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary">Records</Typography>
                    <Typography variant="h4">{analytics.summary.record_count}</Typography>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card><CardContent>
                    <Typography color="text.secondary">Average per record</Typography>
                    <Typography variant="h4">{analytics.summary.average_weight_kg.toFixed(3)} kg</Typography>
                  </CardContent></Card>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Paper sx={{ p: 2, height: 390 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {period === 'monthly' ? `Monthly waste by type (${year})` : 'Yearly waste by type'}
                    </Typography>
                    {(analytics.trend.length > 0) ? (
                      <ResponsiveContainer width="100%" height="90%">
                        <BarChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="label" />
                          <YAxis />
                          <Tooltip formatter={(value) => `${Number(value ?? 0).toFixed(3)} kg`} />
                          <Legend />
                          <Bar dataKey="cut_waste" name="Cut waste (kg)" fill="#00695c" />
                          <Bar dataKey="cotton_waste" name="Cotton waste (kg)" fill="#00a9e0" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ height: '85%', display: 'grid', placeItems: 'center' }}>
                        <Typography color="text.secondary">No records for this period.</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 2, height: 390 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Waste by section</Typography>
                    {sectionData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="90%">
                        <BarChart data={sectionData} layout="vertical" margin={{ left: 12 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis type="category" dataKey="name" width={105} />
                          <Tooltip formatter={(value) => `${Number(value ?? 0).toFixed(3)} kg`} />
                          <Bar dataKey="weight_kg" name="Weight (kg)" fill="#ef6c00" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box sx={{ height: '85%', display: 'grid', placeItems: 'center' }}>
                        <Typography color="text.secondary">No section data for this period.</Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>

              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Waste type</TableCell>
                      <TableCell align="right">Records</TableCell>
                      <TableCell align="right">Total weight (kg)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.byType.map((row) => (
                      <TableRow key={row.waste_type}>
                        <TableCell>{WASTE_TYPE_LABELS[row.waste_type]}</TableCell>
                        <TableCell align="right">{row.record_count}</TableCell>
                        <TableCell align="right">{row.total_weight_kg.toFixed(3)}</TableCell>
                      </TableRow>
                    ))}
                    {analytics.byType.length === 0 && (
                      <TableRow><TableCell colSpan={3} align="center">No waste data for this period.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default CutWasteManagement;